import { Component, ElementRef } from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';

@Component({
  selector: 'formly-expansion-panel-wrapper',
  template: `
    <div *ngIf="props.groupHeading" class="group-heading">{{ props.groupHeading }}</div>
    <mat-expansion-panel [(expanded)]=props.expanded [ngClass]="props.expClass" (afterExpand)="onAfterExpand()">
      <mat-expansion-panel-header>
        <mat-panel-title>
          <span *ngIf="props.icon" class="panel-icon" [class.panel-icon--missing]="props.hasMissing">
            <mat-icon>{{ props.icon }}</mat-icon>
          </span>
          <span class="panel-text">
            <label class="tab-label">{{ props.label }}</label>
            <span *ngIf="props.description" class="panel-description">{{ props.description }}</span>
          </span>
        </mat-panel-title>
      </mat-expansion-panel-header>
      <ng-container #fieldComponent></ng-container>
    </mat-expansion-panel>
  `,
  styles: [
    `
      .tab-label {
        font-weight: 500;
        font-size: 14px;
        letter-spacing: 0.5px;
        cursor: inherit;
      }
    `,
    `
      .exp-class {
        margin: 8px 0;
      }
    `,
    `
      mat-panel-title {
        align-items: center;
      }
      .panel-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 32px;
        width: 32px;
        height: 32px;
        margin-right: 12px;
        border-radius: 50%;
        background: rgba(131, 161, 209, 0.15); /* brand #83a1d1 tint */
        color: #5b7bb0;
      }
      .panel-icon--missing {
        background: rgba(211, 47, 47, 0.12); /* Error Red #d32f2f tint */
        color: #d32f2f;
      }
      .panel-icon .mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
      .panel-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
        line-height: 1.3;
      }
      .panel-description {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.54);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .mat-expansion-panel-header {
        height: auto !important;
        min-height: 56px;
        padding-top: 8px;
        padding-bottom: 8px;
      }
    `,
    `
      /* One heading per family (e.g. Primary Applicant -> Details / Banking) */
      .group-heading {
        margin: 16px 4px 6px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.8px;
        text-transform: uppercase;
        color: rgba(0, 0, 0, 0.54);
      }
      /* Panels of the same family are joined into a single card */
      .mat-expansion-panel.exp-group--first {
        margin-top: 0;
        margin-bottom: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
      .mat-expansion-panel.exp-group--last {
        margin-top: 0;
        border-top: 1px solid #e4e4e8;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    `,
  ],
})
export class ExpansionPanelWrapper extends FieldWrapper {
  constructor(private el: ElementRef<HTMLElement>) {
    super();
  }

  /** props.expandIntent is a one-shot override for programmatic expands: 'silent' skips the scroll, 'focusInvalid' focuses the first invalid control. */
  onAfterExpand(): void {
    const intent = this.props.expandIntent;
    this.props.expandIntent = undefined;
    if (intent === 'silent') return;
    if (intent === 'focusInvalid') {
      this.focusFirstInvalid();
      return;
    }
    this.scrollToGroupHeading();
  }

  private focusFirstInvalid(): void {
    const control = this.el.nativeElement.querySelector<HTMLElement>(
      '.mat-form-field-invalid :is(input, textarea, [tabindex]:not([tabindex="-1"]))'
    );
    if (!control) return;
    control.focus({ preventScroll: true });
    control.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  /** On expand, scroll the nearest preceding group heading (e.g. "Co-Applicant") to the top of the scroll area. */
  scrollToGroupHeading(): void {
    const host = this.el.nativeElement;
    const container = this.getScrollParent(host);
    const headings = Array.from(container.querySelectorAll<HTMLElement>('.group-heading'));
    const heading = headings
      .filter(h => host.contains(h) || !!(h.compareDocumentPosition(host) & Node.DOCUMENT_POSITION_FOLLOWING))
      .pop();
    if (!heading) return;

    const offset = heading.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTo({ top: container.scrollTop + offset, behavior: 'smooth' });
  }

  private getScrollParent(node: HTMLElement): HTMLElement {
    let parent = node.parentElement;
    while (parent) {
      const { overflowY } = getComputedStyle(parent);
      if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return (document.scrollingElement as HTMLElement) || document.documentElement;
  }
}
